// @wc-ignore-file

import type { CrPriority } from "@shared/types/supabase.types";
import { CR_PRIORITIES } from "./change-requests.constants";

/**
 * Parse the `priority` search param of the CR board route (CR-44) into a
 * validated multi-select. Accepts an array (tanstack-router JSON search) or a
 * bare string (hand-typed `?priority=urgent`), drops unknown values and
 * duplicates, and collapses to `undefined` — "no filter" — when nothing valid
 * remains so the param disappears from the URL.
 */
export function parsePrioritySearch(value: unknown): CrPriority[] | undefined {
  const candidates = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];

  const valid = CR_PRIORITIES.filter((priority) => candidates.includes(priority));
  return valid.length > 0 ? valid : undefined;
}

/**
 * Client-side board filter. No selection (undefined or empty) shows every CR,
 * including priority "none" — "none" is only excluded when other priorities
 * are explicitly selected without it.
 */
export function filterCrsByPriority<T extends { priority: CrPriority }>(
  crs: T[],
  selected: CrPriority[] | undefined,
): T[] {
  if (!selected || selected.length === 0) return crs;
  return crs.filter((cr) => selected.includes(cr.priority));
}

/** Toggle one priority in the current selection, normalising back to
 * `undefined` when the selection empties. */
export function togglePriority(
  selected: CrPriority[] | undefined,
  priority: CrPriority,
): CrPriority[] | undefined {
  const current = selected ?? [];
  const next = current.includes(priority)
    ? current.filter((p) => p !== priority)
    : // Keep CR_PRIORITIES order regardless of click order.
      CR_PRIORITIES.filter((p) => current.includes(p) || p === priority);
  return next.length > 0 ? next : undefined;
}
